import React, { useState } from "react";
import "../../../css/ViewCenterModal.css";
import EditCenterModal from "./EditCenterModal";
import ConfirmationModal from "./ConfirmationModal";
import api from "../../../api/axiosConfig";
import { showToast } from '../../Shared/Toaster';


interface WorkingTiming {
  day: string;
  startTime: string;
  endTime: string;
}

interface Holiday {
  holidayName: string;
  day: string;
  date: string;
}

interface Center {
  _id: string;
  name: string;
  centerId?: string;
  description?: string;
  centerType: string;
  city: string;
  isActive: boolean;
  createdAt: string;
  disabledOn?: string;
  addressLine1?: string;
  addressLine2?: string;
  latitude?: number;
  longitude?: number;
  telephone1?: string;
  telephone2?: string;
  workingTimings?: WorkingTiming[];
  holidays?: Holiday[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  center: Center | null;
  onUpdate?: (center: any) => void; // optional callback
}

const ViewCenterModal: React.FC<Props> = ({
  isOpen,
  onClose,
  center,
  onUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);

  if (!isOpen || !center) return null;

  // Handler for the confirmation
  const handleDisableConfirm = async () => {
    if (!center) return;

    setIsDisabling(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) {
        showToast("Authentication error: No access token found.", 'error');
        setIsDisabling(false);
        return;
      }

      // ---***---
      // CORRECTION: Calling the correct endpoint and sending centerId in the body
      // ---***---
      await api.center_api.patch(
        `/api/v1/centers/toggle-active`, // Corrected URL
        { centerId: center._id }, // Corrected: Sending ID in the body
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      showToast(
        `Center ${center.isActive ? "disabled" : "enabled"} successfully!`
        , 'success');

      // If onUpdate prop exists, call it to refresh data in parent
      if (onUpdate) {
        // Create an updated version of the center to pass back
        const updatedCenter = { ...center, isActive: !center.isActive };
        onUpdate(updatedCenter);
      }

      setShowDisableConfirm(false);
      onClose(); // Close the main modal after action
    } catch (error) {
      console.error("Failed to toggle center status:", error);
      showToast("Error: Could not update the center status. Please try again.", 'error');
    } finally {
      setIsDisabling(false);
    }
  };

  // If editing, show EditCenterModal
  if (isEditing) {
    return (
      <EditCenterModal
        isOpen={true}
        onClose={() => {
          setIsEditing(false);
          onClose();
        }}
        center={center}
        onUpdate={onUpdate}
      />
    );
  }

  return (
    <React.Fragment>
      <div className="view-center-modal-overlay" onClick={onClose}>
        <div className="view-center-modal-container-view-center" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2 className="view-center-section-title">View Center</h2>
            <button className="global-close-btn" onClick={onClose}>✕</button>
          </div>

          {/* Basic Information */}
          <div className="view-center-section">
            <h4 className="view-center-sub-title">Basic Information</h4>
            <div className="view-center-info-grid">
              <div>
                <label>Center Name</label>
                <p className="view-center-value">{center.name}</p>
              </div>
              <div>
                <label>Address Line 1</label>
                <p className="view-center-value">{center.addressLine1 || "-"}</p>
              </div>
              <div>
                <label>Address Line 2</label>
                <p className="view-center-value">{center.addressLine2 || "-"}</p>
              </div>
              <div>
                <label>City</label>
                <p className="view-center-value">{center.city}</p>
              </div>
              <div>
                <label>Status</label>
                <p className="view-center-value">
                  {center.isActive ? "Active" : "Disabled"}
                </p>
              </div>
              <div>
                <label>Telephone 1</label>
                <p className="view-center-value">{center.telephone1 || "-"}</p>
              </div>
              <div>
                <label>Telephone 2</label>
                <p className="view-center-value">{center.telephone2 || "-"}</p>
              </div>
              <div>
                <label>Latitude</label>
                <p className="view-center-value">{center.latitude || "-"}</p>
              </div>
              <div>
                <label>Longitude</label>
                <p className="view-center-value">{center.longitude || "-"}</p>
              </div>
              <div>
                <label>Created On</label>
                <p className="view-center-value">
                  {new Date(center.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <label>Disabled On</label>
                <p className="view-center-value">
                  {center.disabledOn
                    ? new Date(center.disabledOn).toLocaleDateString()
                    : "-"}
                </p>
              </div>
            </div>
          </div>

          {/* Working Timings */}
          <div className="view-center-section">
            <h4 className="view-center-sub-title">Working Timings</h4>
            <table className="view-center-styled-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                </tr>
              </thead>
              <tbody>
                {center.workingTimings?.length ? (
                  center.workingTimings.map((wt, i) => (
                    <tr key={i}>
                      <td>{wt.day}</td>
                      <td>{wt.startTime}</td>
                      <td>{wt.endTime}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="empty">
                      No timings available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Holidays */}
          <div className="view-center-section">
            <h4 className="view-center-sub-title">Holiday Calendar</h4>
            <table className="view-center-styled-table">
              <thead>
                <tr>
                  <th>Holiday Name</th>
                  <th>Day</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {center.holidays?.length ? (
                  center.holidays.map((h, i) => (
                    <tr key={i}>
                      <td>{h.holidayName}</td>
                      <td>{h.day}</td>
                      <td>{h.date}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="empty">
                      No holidays listed
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="view-center-modal-footer">
            <button className="view-center-btn close" onClick={onClose}>
              Close
            </button>
            <button
              className="view-center-btn edit"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
            <button
              className={`view-center-btn ${center.isActive ? "disable" : "edit"
                }`}
              onClick={() => setShowDisableConfirm(true)}
              disabled={isDisabling}
            >
              {isDisabling
                ? center.isActive
                  ? "Disabling..."
                  : "Enabling..."
                : center.isActive
                  ? "Disable"
                  : "Enable"}
            </button>
          </div>
        </div>
      </div>

      {/* Conditional Confirmation Modal */}
      {showDisableConfirm && (
        <ConfirmationModal
          message={`Are you sure you want to ${center.isActive ? "disable" : "enable"
            } this center?`}
          onConfirm={handleDisableConfirm}
          onCancel={() => setShowDisableConfirm(false)}
        />
      )}
    </React.Fragment>
  );
};

export default ViewCenterModal;